import React from 'react';
import { Box, TextField, FormControlLabel, Switch, Chip, Autocomplete, Checkbox } from '@mui/material';
import type { PadelSettings, PadelTieBreaker } from '@/types';
import type { SportSettingsFormProps } from './types';
import { SettingsSection } from './SettingsSection';
import { SettingsField, SettingsFieldsGrid } from './SettingsFieldsGrid';

const TIEBREAKER_OPTIONS: { value: PadelTieBreaker; label: string }[] = [
  { value: 'sets_ratio',   label: 'Соотношение сетов' },
  { value: 'games_ratio',  label: 'Соотношение геймов' },
  { value: 'head_to_head', label: 'Личные встречи' },
];

export const PadelSettingsForm: React.FC<SportSettingsFormProps> = ({ value, onChange, disabled }) => {
  const s = value as PadelSettings;
  const upd = (patch: Partial<PadelSettings>) => onChange({ ...s, ...patch });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      <SettingsSection title="Структура матча">
        <SettingsFieldsGrid>
          <SettingsField>
            <TextField label="Сетов в матче" type="number" size="small" fullWidth
              value={s.sets ?? 3}
              onChange={(e) => upd({ sets: parseInt(e.target.value) || 3 })}
              slotProps={{ htmlInput: { min: 1, max: 5, step: 2 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Тай-брейк при счёте x:x" type="number" size="small" fullWidth
              value={s.tiebreakAt ?? 6}
              onChange={(e) => upd({ tiebreakAt: parseInt(e.target.value) || 6 })}
              slotProps={{ htmlInput: { min: 4 } }}
              disabled={disabled} />
          </SettingsField>
        </SettingsFieldsGrid>
      </SettingsSection>

      <SettingsSection title="Правила">
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 1 }}>
          <FormControlLabel
            control={<Switch checked={s.superTiebreak ?? true}
              onChange={(e) => upd({ superTiebreak: e.target.checked })} disabled={disabled} />}
            label="Матч-тай-брейк в решающем сете (7 очков)"
          />
          <FormControlLabel
            control={<Switch checked={s.goldenPoint ?? true}
              onChange={(e) => upd({ goldenPoint: e.target.checked })} disabled={disabled} />}
            label="Золотое очко (ровно)"
          />
        </Box>
      </SettingsSection>

      <SettingsSection title="Очки в таблице">
        <SettingsFieldsGrid>
          <SettingsField>
            <TextField label="За победу" type="number" size="small" fullWidth
              value={s.pointsWin ?? 2}
              onChange={(e) => upd({ pointsWin: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="За поражение" type="number" size="small" fullWidth
              value={s.pointsLoss ?? 0}
              onChange={(e) => upd({ pointsLoss: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
        </SettingsFieldsGrid>
      </SettingsSection>

      <SettingsSection title="Тай-брейки">
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={TIEBREAKER_OPTIONS.map((o) => o.value)}
          value={s.tieBreakers ?? []}
          onChange={(_, v) => upd({ tieBreakers: v as PadelTieBreaker[] })}
          getOptionLabel={(o) => TIEBREAKER_OPTIONS.find((x) => x.value === o)?.label ?? o}
          disabled={disabled}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
              {TIEBREAKER_OPTIONS.find((x) => x.value === option)?.label ?? option}
            </li>
          )}
          renderTags={(val, getTagProps) =>
            val.map((opt, idx) => (
              <Chip label={`${idx + 1}. ${TIEBREAKER_OPTIONS.find((x) => x.value === opt)?.label ?? opt}`}
                size="small" {...getTagProps({ index: idx })} key={opt} />
            ))
          }
          renderInput={(params) => <TextField {...params} size="small" fullWidth placeholder="Добавить критерий..." />}
        />
      </SettingsSection>

    </Box>
  );
};
